import type { QuizAssignedDataWithAttempts } from '@repo/api/quiz/quizService';
import type { AxiosError } from 'axios';
import type { Dispatch, SetStateAction } from 'react';

import { useDeleteQuiz } from '@repo/api/quiz/useDeleteQuiz';
import { quizErrorMessages } from '@repo/lib/messages/quizErrorMessages';
import { BookText } from 'lucide-react';
import { useEffect, useState } from 'react';

import DeleteQuizErrorAlert from '@/components/features/manage/manage-quizzes/DeleteQuizErrorAlert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogBody, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

interface DeleteQuizProps {
  quizzes?: QuizAssignedDataWithAttempts[] | null;
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

interface QuizError {
  quizId: string;
  error: Error | AxiosError | unknown;
}

interface UseDeleteQuizLogicProps {
  quizzes?: QuizAssignedDataWithAttempts[] | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function useDeleteQuizLogic(useDeleteQuizLogicProps: UseDeleteQuizLogicProps) {
  const { quizzes, open, onOpenChange } = useDeleteQuizLogicProps;
  const [quizErrors, setQuizErrors] = useState<QuizError[]>([]);
  const [tryingForce, setTryingForce] = useState<string[]>([]);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(false);

  const { mutateAsync, reset, isPending } = useDeleteQuiz();

  const isSingle: boolean = quizzes?.length === 1;
  const quizIds = quizzes?.map((quiz) => quiz.id);

  const onClose = () => {
    onOpenChange(false);
    setQuizErrors([]);
    setTryingForce([]);
    setSubmitButtonDisabled(false);
  };

  const deleteQuiz = async (quizId: string, force = false) => {
    try {
      await mutateAsync({ quizId, force });
      setQuizErrors((prev) => prev.filter((e) => e.quizId !== quizId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const onSubmit = async () => {
    const errors: QuizError[] = [];
    if (!quizIds) return;
    for (const quizId of quizIds) {
      const result = await deleteQuiz(quizId, false);
      if (!result.success) {
        errors.push({ quizId, error: result.error });
      }
    }

    setQuizErrors(errors);

    if (errors.length === 0) {
      onClose();
    } else {
      setSubmitButtonDisabled(true);
    }
  };

  const onForceDelete = async (quizId: string) => {
    setTryingForce((prev) => [...prev, quizId]);

    const result = await deleteQuiz(quizId, true);

    if (!result.success) {
      toasterMessageHandler(quizErrorMessages, result.error as Error | AxiosError);
    }

    setTryingForce((prev) => prev.filter((id) => id !== quizId));
  };

  const dismissError = (quizId: string) => {
    setQuizErrors((prev) => prev.filter((e) => e.quizId !== quizId));
  };

  const getQuizName = (quizId: string) => {
    const quiz = quizzes?.find((q) => q.id === quizId);
    return quiz?.name || 'Quiz';
  };

  const isQuizBeingForceDeleted = (quizId: string) => tryingForce.includes(quizId);

  useEffect(() => {
    if (open) {
      reset();
      setQuizErrors([]);
      setTryingForce([]);
      setSubmitButtonDisabled(false);
    }
  }, [open, reset]);

  useEffect(() => {
    if (submitButtonDisabled && quizErrors.length === 0 && tryingForce.length === 0) {
      onClose();
    }
  }, [submitButtonDisabled, quizErrors.length, tryingForce.length]);

  return {
    // State
    state: {
      submitButtonDisabled,
      quizErrors,
      isPending,
      quizzes,
      isSingle,
    },
    actions: {
      // Actions
      onSubmit,
      onClose,
      onForceDelete,
      dismissError,

      // Helpers
      getQuizName,
      isQuizBeingForceDeleted,
    },
  };
}

export default function DeleteQuiz(deleteQuizProps: DeleteQuizProps) {
  const { quizzes, open, onOpenChange } = deleteQuizProps;
  const { state, actions } = useDeleteQuizLogic(deleteQuizProps);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <BookText />
            </FeaturedIcon>
            <div className="flex min-w-0 grow flex-col gap-0.5">
              <AlertDialogTitle>Usunąć Quiz/y?</AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground text-xs">Usuń wybrane quiz/y.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>

          <div className="flex flex-col gap-3 overflow-auto p-4">
            {!state.isSingle ? (
              <p className="text-text-secondary">Czy na pewno chcesz usunąć wybrane quizy?</p>
            ) : (
              <p className="text-text-secondary">
                Czy na pewno chcesz usunąć quiz <b>{quizzes?.[0]?.name}</b>?
              </p>
            )}

            {!state.isSingle && (
              <div className="flex flex-col gap-3">
                <h5 className="text-text-tertiary text-sm font-medium">Quizy:</h5>
                <div className="flex flex-wrap gap-2">
                  {state.quizzes?.map(({ name, id }) => {
                    const hasError = state.quizErrors.map((e) => e.quizId).includes(id);
                    return (
                      <Badge key={id} size="sm" variant={hasError ? 'destructive' : 'muted'}>
                        {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {state.quizErrors.map(({ quizId, error }) => (
              <DeleteQuizErrorAlert
                key={quizId}
                quizId={quizId}
                quizName={actions.getQuizName(quizId)}
                error={error}
                isForceDeleting={actions.isQuizBeingForceDeleted(quizId)}
                onForceDelete={actions.onForceDelete}
                onDismiss={actions.dismissError}
              />
            ))}
          </div>
        </AlertDialogBody>

        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant="tertiary" onClick={actions.onClose}>
            Zamknij
          </AlertDialogCancel>

          <Button onClick={actions.onSubmit} disabled={state.isPending} className={state.submitButtonDisabled ? 'hidden' : ''}>
            <LoadingSwap isLoading={state.isPending} className="flex items-center gap-2">
              Potwierdź
            </LoadingSwap>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
